import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Camera, Upload, X, ChevronLeft } from "lucide-react";

const SERVICES = ["CAD", "3D PRINTING", "CASTING", "STONE SETTING", "POLISHING", "REPAIRS", "LASER WELDING", "CLEANING", "OTHER"];
const PIECES = ["RING", "PENDANT", "CHAIN", "BRACELET", "EARRINGS", "GRILLZ", "OTHER"];
const METALS = ["18ct Yellow Gold", "18ct White Gold", "18ct Rose Gold", "9ct Yellow Gold", "10ct Yellow Gold", "950 Platinum", "925 Silver", "Other"];

export default function JobNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [f, setF] = useState({
    member_id: "", client_reference: "", title: "", description: "", services: [], piece_type: "RING",
    metal: "18ct Yellow Gold", metal_weight: "", stone_info: "", dimensions: "", quantity: 1, target_date: "", priority: "STANDARD", notes: "",
  });

  useEffect(() => { if (isAdmin) api.get("/members").then((r) => setMembers(r.data)).catch(() => {}); }, [isAdmin]);

  const toggleService = (s) => setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFiles = (e) => setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Job title is required");
    if (isAdmin && !f.member_id) return toast.error("Select a member");
    if (f.services.length === 0) return toast.error("Select at least one service");
    setSaving(true);
    try {
      const payload = { ...f, quantity: Number(f.quantity) || 1, target_date: f.target_date ? new Date(f.target_date).toISOString() : null };
      const { data: job } = await api.post("/jobs", payload);
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", file.type.startsWith("image") ? "IMAGE" : "DOCUMENT");
        await api.post(`/jobs/${job.id}/files`, fd);
      }
      toast.success(`Job ${job.job_number} submitted`);
      navigate(`/jobs/${job.id}`);
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, children }) => (
    <div><label className="eyebrow block mb-2">{label}</label>{children}</div>
  );
  const inputCls = "w-full panel rounded-sm px-3.5 py-3 text-sm outline-none focus:border-[#C5A059] transition-colors";

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-[#8C867A] hover:text-[#E2DFD7] mb-4"><ChevronLeft size={14} /> Back</button>
      <PageHeader eyebrow="Trade Job" title="New Trade Job" />

      <form onSubmit={submit} className="space-y-6">
        <div className="panel rounded-sm p-5 space-y-4">
          {isAdmin && (
            <Field label="Member">
              <select data-testid="job-member" value={f.member_id} onChange={(e) => set("member_id", e.target.value)} className={inputCls}>
                <option value="">Select bench holder…</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.bench_number}</option>)}
              </select>
            </Field>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Job Title"><input data-testid="job-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="18ct Yellow Gold Signet Ring" /></Field>
            <Field label="Client Reference"><input data-testid="job-client-ref" value={f.client_reference} onChange={(e) => set("client_reference", e.target.value)} className={inputCls} placeholder="Optional" /></Field>
          </div>
          <Field label="Description"><textarea data-testid="job-description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} placeholder="Describe the piece and work required…" /></Field>
        </div>

        <div className="panel rounded-sm p-5 space-y-4">
          <Field label="Services Required (select all that apply)">
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`service-${s}`}
                  className={`px-3 py-2 rounded-sm text-xs uppercase tracking-wider border transition-all ${
                    f.services.includes(s) ? "border-[#C5A059] bg-[rgba(212,175,55,0.12)] text-[#D4AF37]" : "hairline text-[#8C867A] hover:text-[#E2DFD7]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Piece Type">
              <select data-testid="job-piece" value={f.piece_type} onChange={(e) => set("piece_type", e.target.value)} className={inputCls}>
                {PIECES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Metal">
              <select data-testid="job-metal" value={f.metal} onChange={(e) => set("metal", e.target.value)} className={inputCls}>
                {METALS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Metal Weight (if known)"><input value={f.metal_weight} onChange={(e) => set("metal_weight", e.target.value)} className={inputCls} placeholder="e.g. 8.4g" /></Field>
            <Field label="Ring Size / Dimensions"><input value={f.dimensions} onChange={(e) => set("dimensions", e.target.value)} className={inputCls} placeholder="e.g. Size N" /></Field>
            <Field label="Stone Information"><input value={f.stone_info} onChange={(e) => set("stone_info", e.target.value)} className={inputCls} placeholder="e.g. 1x 0.50ct brilliant" /></Field>
            <Field label="Quantity"><input type="number" min="1" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} /></Field>
          </div>
        </div>

        <div className="panel rounded-sm p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Target Completion Date"><input type="date" data-testid="job-target" value={f.target_date} onChange={(e) => set("target_date", e.target.value)} className={inputCls} /></Field>
            <Field label="Priority">
              <div className="flex gap-2">
                {["STANDARD", "URGENT"].map((p) => (
                  <button type="button" key={p} onClick={() => set("priority", p)} data-testid={`priority-${p}`}
                    className={`flex-1 py-3 rounded-sm text-xs uppercase tracking-wider border transition-all ${
                      f.priority === p ? (p === "URGENT" ? "border-[rgba(245,158,11,0.5)] bg-[rgba(245,158,11,0.12)] text-[#F59E0B]" : "border-[#C5A059] bg-[#1A1A1E] text-[#F5F3EF]") : "hairline text-[#8C867A]"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <Field label="Attach Files (photos, CAD, STL, PDF)">
            <div className="flex flex-wrap gap-2 mb-3">
              <label className="btn-brass flex items-center gap-2 px-4 py-3 rounded-sm text-sm cursor-pointer">
                <Camera size={16} /> Take / Choose Photo
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} data-testid="job-photo-input" />
              </label>
              <label className="panel flex items-center gap-2 px-4 py-3 rounded-sm text-sm cursor-pointer hover:border-[#C5A059] transition-colors">
                <Upload size={16} /> Attach Files
                <input type="file" multiple className="hidden" onChange={onFiles} data-testid="job-file-input" />
              </label>
            </div>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 panel rounded-sm px-3 py-1.5 text-xs text-[#C2BEB5]">
                    <span className="truncate max-w-[160px]">{file.name}</span>
                    <X size={13} className="cursor-pointer text-[#8C867A]" onClick={() => removeFile(i)} />
                  </div>
                ))}
              </div>
            )}
          </Field>
          <Field label="Notes"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} placeholder="Any additional notes…" /></Field>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="submit-job" className="btn-brass px-6 py-3 rounded-sm text-sm">{saving ? "Submitting…" : "Submit Trade Job"}</button>
          <button type="button" onClick={() => navigate("/jobs")} className="panel px-6 py-3 rounded-sm text-sm text-[#C2BEB5] hover:border-[#C5A059] transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}
